/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { IManageCodesResponse } from "Interfaces/Benefits/IManageCodesResponse";
import { ISubmitCsvFileResponse } from "Interfaces/Benefits/ISubmitCsvFileResponse";
import { IBenefitUploadCSVFileRequest } from "Interfaces/Benefits/IUploadCSVFileRequest";
import {
  ISubmitCSVFileRequest,
  IUploadCsvFileValidation,
} from "Interfaces/SA/IUploadCSVFileRequest";

import { MANAGECODES_BASE_URL } from "../Constants/SagaConstants";
import { IManageCodesRequest } from "../Interfaces/Benefits/IManageCodesRequest";
import { getAuthToken } from "./ApiUtils";

/**
 * ManageCodesApi to create endpoints for api service calls. Authenticating with the token and appending in headers for all calls.
 */
export const manageCodesApi = createApi({
  reducerPath: "manageCodesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: MANAGECODES_BASE_URL,
    prepareHeaders: async (headers) => {
      // Use the getAuthToken utility to fetch and add the token
      return await getAuthToken(headers);
    },
  }),
  endpoints: (builder) => ({
    getBenefitNames: builder.query<string, string>({
      query: (tenant: string) => `/${tenant}/managecodes/benefitnames`, // This is the endpoint to get the benefit names
    }),
    getManageCodesListResponse: builder.query<
      IManageCodesResponse,
      { tenant: string; query: IManageCodesRequest }
    >({
      query: ({ tenant, query }) => {
        return {
          url: `/${tenant}/managecodes/benefituploads`,
          method: "POST",
          body: query,
        };
      },
    }),
    uploadCsvFile: builder.mutation<
      IUploadCsvFileValidation,
      { tenant: string; request: IBenefitUploadCSVFileRequest }
    >({
      query: ({ tenant, request }) => ({
        url: `/${tenant}/managecodes/validatecsv`,
        method: "POST",
        body: request,
      }),
    }),
    submitCsvFile: builder.mutation<
      ISubmitCsvFileResponse,
      { tenant: string; request: ISubmitCSVFileRequest }
    >({
      query: ({ tenant, request }) => ({
        url: `/${tenant}/managecodes/submitcsv`,
        method: "POST",
        body: request,
      }),
    }),
  }),
});

export const {
  useGetBenefitNamesQuery,
  useGetManageCodesListResponseQuery,
  useUploadCsvFileMutation,
  useSubmitCsvFileMutation,
} = manageCodesApi;
export const { getBenefitNames, getManageCodesListResponse } = manageCodesApi.endpoints;
export default manageCodesApi.reducer;
